import { AnyAction } from 'redux';
import {
  GlobalActions,
  TiergruppeAnimal,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import { FamilienStand, Tarifbereich, ThhTier, Tierart } from '@ps20/bff-models';
import {
  App,
  determineAssekuranztarifFromTarifbereich,
  ThhHundVorbelegungdaten,
  ValueLabelElement,
} from '@ps20-frontend/frontend-models';
import { RasseMehrereHundeChangedPayload, ThhHundActions } from '../thhHundActions';

export enum AnzahlTiereEnum {
  EINES = 'EIN_TIER',
  MEHRERE = 'MEHRERE_TIERE',
}

export const initialAnzahlMehrereHunde = 2;
export const maximalAnzahlHundeOhneUnderwriter = 10;

export interface Versicherungsnehmer {
  geburtsdatum: string;
  familienstand: FamilienStand;
  isAssekuranztarif: boolean;
  tarifbereich?: Tarifbereich;
  anzahlTiere: AnzahlTiereEnum;
  rasseEinHund: string;
  hunderassenAuswahlElemente: Array<ValueLabelElement>;
  anzahlMehrereHunde: number;
  rasseMehrereHunde: string[];
  tiere?: ThhTier[];
  tiergruppeAnimals?: TiergruppeAnimal[];
}

export const versicherungsnehmerReducer = function (
  sectionState: Versicherungsnehmer = null,
  action: AnyAction,
): Versicherungsnehmer {
  switch (action.type) {
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      if (action.payload.versicherungsnehmer?.geburtsdatum) {
        const dateSplitted = action.payload.versicherungsnehmer.geburtsdatum.split('-');
        const dateAsString = `${dateSplitted[2]}.${dateSplitted[1]}.${dateSplitted[0]}`;
        return {
          ...sectionState,
          geburtsdatum: dateAsString,
        };
      } else {
        return sectionState;
      }
    }
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA_AFTER_VV_LOAD:
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA: {
      return {
        ...sectionState,
        isAssekuranztarif: action.payload,
      };
    }
    case ThhHundActions.GEBURTSDATUM_CHANGED: {
      const geburtsdatum: string = action.payload;
      return { ...sectionState, geburtsdatum };
    }
    case ThhHundActions.ANZAHL_TIERE_CHANGED: {
      const anzahlTiere: AnzahlTiereEnum = action.payload;
      if (anzahlTiere === AnzahlTiereEnum.EINES) {
        return {
          ...sectionState,
          anzahlTiere,
          anzahlMehrereHunde: initialAnzahlMehrereHunde,
          rasseMehrereHunde: new Array(initialAnzahlMehrereHunde),
        };
      }
      return { ...sectionState, anzahlTiere };
    }
    case GlobalActions.THH_HUND_VORBELEGUNG_FINISHED: {
      const actionPayload: VorbelegungPayload = action.payload;
      if (actionPayload.appId === App.THH_HUND) {
        const vorhandeneVorbelegungdaten: ThhHundVorbelegungdaten =
          action.payload.vorhandeneVorbelegungdaten;
        return {
          ...sectionState,
          tiere: vorhandeneVorbelegungdaten.tiere,
        };
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: ThhHundVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.THH_HUND) {
        return {
          ...sectionState,
          tiere: actionPayload.tiere,
        };
      }
      return sectionState;
    }
    case GlobalActions.TARIFBEREICH_CHANGED: {
      return {
        ...sectionState,
        tarifbereich: action.payload,
        isAssekuranztarif: determineAssekuranztarifFromTarifbereich(action.payload),
      };
    }
    case ThhHundActions.RASSE_EIN_HUND_CHANGED: {
      const rasseEinHund: string = action.payload;
      return { ...sectionState, rasseEinHund };
    }
    case GlobalActions.THH_HUNDERASSEN_LISTE_RETRIEVED: {
      const hunderassenAuswahlElemente: Array<ValueLabelElement> = action.payload;
      return { ...sectionState, hunderassenAuswahlElemente };
    }
    case ThhHundActions.ANZAHL_MEHRERE_HUNDE_CHANGED: {
      const anzahlMehrereHunde: number = action.payload;
      const rasseMehrereHunde = [...sectionState.rasseMehrereHunde];
      rasseMehrereHunde.length = Math.min(anzahlMehrereHunde, maximalAnzahlHundeOhneUnderwriter);
      return { ...sectionState, anzahlMehrereHunde, rasseMehrereHunde };
    }
    case ThhHundActions.RASSE_MEHRERE_HUNDE_CHANGED: {
      const newRasseMehrereHundeEntry: RasseMehrereHundeChangedPayload = action.payload;
      const newRasseMehrereHunde = [...sectionState.rasseMehrereHunde];
      newRasseMehrereHunde[newRasseMehrereHundeEntry.index] = newRasseMehrereHundeEntry.rasse;
      if (!sectionState.tiere) {
        sectionState.tiere = [];
        newRasseMehrereHunde.forEach(() =>
          sectionState.tiere.push({ tierart: Tierart.VRHUND } as ThhTier),
        );
      }
      return {
        ...sectionState,
        rasseMehrereHunde: newRasseMehrereHunde,
        tiere: sectionState?.tiere.map((tier, index) => {
          if (index === newRasseMehrereHundeEntry.index) {
            tier.tierrasse = newRasseMehrereHundeEntry.rasse;
          }
          return tier;
        }),
      };
    }
    default:
      return sectionState;
  }
};
